import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router'

import { errorTypes } from 'utils/enums/errorTypes'
import { API } from 'utils/api/api'
import { Toastr } from 'utils/toastr/Toastr'
import { formatBEError } from 'utils/helpers/BEError'
import { CreateAccountView } from 'containers/createAccount/CreateAccountView'
import { createAccountFormConfig } from 'containers/createAccount/formConfig'
import { useForm } from 'hooks/useForm'
import { FormFieldsData } from 'types/form'
import { useCreateAccountModals } from './hooks/useCreateAccountModals'

const getJobPostingDetails = () => ({
  jobPostingDetails: {
    jobPostingId: localStorage.getItem('masterId') || null,
    countyId: localStorage.getItem('countyId') || null,
    districtId: localStorage.getItem('districtId') || null,
  },
})

const getCreateAccountRequestPayload = (formData: FormFieldsData) => ({
  ...getJobPostingDetails(),
  ...formData,
})

export const CreateAccountContainer = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [isCreationPending, setIsCreationPending] = useState(false)
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(location.pathname === '/welcome')
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false)
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false)
  const [isBlurred, setIsBlurred] = useState(isWelcomeModalOpen)

  const { formData, formMeta, handleFieldChange, getFormData, setFormElementRef, handleFormSubmit } = useForm({
    formConfig: createAccountFormConfig,
  })

  const handleShowCreateAccountForm = useCallback(() => {
    setIsBlurred(false)

    navigate({
      pathname: '/create-account',
      search: window.location.search,
    })
  }, [navigate])

  const handleCreateAccount = useCallback(() => {
    setIsCreationPending(true)

    API.makeRequest({
      type: 'post',
      requestUrl: '/auth/accounts',
      requestData: getCreateAccountRequestPayload(getFormData()),
    })
      .then(() => setIsVerifyEmailModalOpen(true))
      .catch(({ response }) => {
        const blockedAccountError = formatBEError([errorTypes.ACCOUNT_0014], response)
        const missedJobPostingError = formatBEError([errorTypes.ACCOUNT_0010], response)

        handleFormSubmit(response)

        if (blockedAccountError) {
          setIsBlockedModalOpen(true)
        }

        if (missedJobPostingError) {
          Toastr.error(missedJobPostingError)
        } else {
          Toastr.error('Failed to create account.')
        }
      })
      .finally(() => setIsCreationPending(false))
  }, [getFormData, handleFormSubmit])

  const handleSendNewCode = useCallback(
    () =>
      API.makeRequest({
        type: 'post',
        requestUrl: '/auth/accounts',
        requestData: getCreateAccountRequestPayload(getFormData()),
      }),
    [getFormData],
  )

  const handleRouterOnEnter = useCallback(() => {
    if (location.pathname === '/welcome' && !isWelcomeModalOpen) {
      setIsWelcomeModalOpen(true)
      setIsBlurred(true)
    }
  }, [location.pathname, isWelcomeModalOpen])

  const prevLocationRef = useRef(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      handleRouterOnEnter()
      isInitialMount.current = false
    } else {
      if (location.key !== prevLocationRef.current?.key) {
        handleRouterOnEnter()
      }
    }

    prevLocationRef.current = location
  }, [location, location.key, handleRouterOnEnter])

  const { Modals, handlePrivacyPolicyModalOpen } = useCreateAccountModals({
    isBlockedModalOpen,
    setIsBlockedModalOpen,
    setIsWelcomeModalOpen,
    handleShowCreateAccountForm,
    isWelcomeModalOpen,
    formData,
    isVerifyEmailModalOpen,
    setIsVerifyEmailModalOpen,
    handleSendNewCode,
  })

  return (
    <CreateAccountView
      formData={formData}
      formMeta={formMeta}
      handleFieldChange={handleFieldChange}
      setFormElementRef={setFormElementRef}
      handleCreateAccount={handleCreateAccount}
      isBlurred={isBlurred}
      isCreationPending={isCreationPending}
      Modals={Modals}
      handlePrivacyPolicyModalOpen={handlePrivacyPolicyModalOpen}
    />
  )
}
